import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { I18n, Translate } from 'react-redux-i18n';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import RadioGroup from '@material-ui/core/RadioGroup';
import Radio from '@material-ui/core/Radio';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import InputAdornment from '@material-ui/core/InputAdornment';
import { confirm } from '../utils';

// Bulk "Prix d'achat HT" edit for the products selected in the Quantités
// grid — same "quick config" spirit as PriceCategoryInit, but applied to a
// user-picked set of rows instead of every price category at once.
class PurchasePriceBulkEdit extends Component {
	constructor(props) {
		super(props);
		this.state = {
			mode: 'discount',
			fixedValue: '',
			discountType: 'coefficient',
			discountValue: '',
		};
	}

	componentDidUpdate(prevProps) {
		if (!prevProps.open && this.props.open) {
			this.setState({
				mode: 'discount',
				fixedValue: '',
				discountType: 'coefficient',
				discountValue: '',
			});
		}
	}

	sanitizeNumber = value => value.replace(/,/g, '.').replace(/[^.\d]/g, '');

	// tax.rate is stored as a fraction in DB (0.10 for 10%, see TaxForm.js
	// `onValid` — "tax rate between 0 and 1 in DB"), not as a 0-100 percentage.
	resolveRate = id_tax_rate => {
		const tax = (this.props.taxes || []).find(t => t.id === id_tax_rate);
		return tax ? Number(tax.rate) : 0;
	};

	computeSaleHt = product => {
		const rate = this.resolveRate(product.id_tax_rate);
		const priceTtc = Number(product.price) || 0;
		return priceTtc / (1 + rate);
	};

	computeItems = () => {
		const { mode, fixedValue, discountType, discountValue } = this.state;
		const { selectedProducts } = this.props;

		if (mode === 'fixed') {
			const value = parseFloat(fixedValue);
			if (!Number.isFinite(value) || value < 0) return [];
			return selectedProducts.map(p => ({
				id_product: p.id_global,
				unit_value: Number(value.toFixed(2)),
			}));
		}

		const value = parseFloat(discountValue);
		if (!Number.isFinite(value) || value < 0) return [];
		if (discountType === 'coefficient' && value === 0) return [];

		return selectedProducts.map(p => {
			const saleHt = this.computeSaleHt(p);
			let raw;
			if (discountType === 'amount') {
				raw = saleHt - value;
			} else if (discountType === 'percent') {
				raw = saleHt * (1 - value / 100);
			} else {
				raw = saleHt / value;
			}
			// Only the final stored value is rounded (to 2 decimals, matching
			// every other purchase-price display/storage in this feature) —
			// the division/percentage itself is never rounded mid-calculation.
			return {
				id_product: p.id_global,
				unit_value: Number(Math.max(0, raw).toFixed(2)),
			};
		});
	};

	handleValid = () => {
		const items = this.computeItems();
		if (!items.length) return;

		const label = I18n.t('productStock.bulkPurchasePrice.confirmation', {
			n: items.length,
		});
		confirm(label).then(() => {
			this.props.onValid(items);
		});
	};

	render() {
		const { open, onCancel, selectedProducts } = this.props;
		const { mode, fixedValue, discountType, discountValue } = this.state;
		const items = this.computeItems();
		const canValid = items.length > 0;

		return (
			<Dialog open={open} onClose={onCancel} maxWidth={'sm'} fullWidth>
				<DialogTitle>
					{I18n.t('productStock.bulkPurchasePrice.title', {
						n: selectedProducts.length,
					})}
				</DialogTitle>
				<DialogContent>
					<RadioGroup
						value={mode}
						onChange={ev => this.setState({ mode: ev.target.value })}>
						<FormControlLabel
							value="fixed"
							control={<Radio />}
							label={I18n.t('productStock.bulkPurchasePrice.modeFixed')}
						/>
						{mode === 'fixed' && (
							<div style={{ marginLeft: 32, marginBottom: 16 }}>
								<TextField
									value={fixedValue}
									onChange={ev =>
										this.setState({ fixedValue: this.sanitizeNumber(ev.target.value) })
									}
									style={{ width: 160 }}
									InputProps={{
										endAdornment: <InputAdornment position="end">€ HT</InputAdornment>,
									}}
								/>
							</div>
						)}

						<FormControlLabel
							value="discount"
							control={<Radio />}
							label={I18n.t('productStock.bulkPurchasePrice.modeDiscount')}
						/>
						{mode === 'discount' && (
							<div style={{ marginLeft: 32, marginBottom: 16 }}>
								<RadioGroup
									row
									value={discountType}
									onChange={ev => this.setState({ discountType: ev.target.value })}
									style={{ marginBottom: 8 }}>
									<FormControlLabel
										value="coefficient"
										control={<Radio />}
										label={I18n.t('productStock.bulkPurchasePrice.discountCoefficient')}
									/>
									<FormControlLabel
										value="amount"
										control={<Radio />}
										label={I18n.t('productStock.bulkPurchasePrice.discountAmount')}
									/>
									<FormControlLabel
										value="percent"
										control={<Radio />}
										label={I18n.t('productStock.bulkPurchasePrice.discountPercent')}
									/>
								</RadioGroup>
								<TextField
									value={discountValue}
									onChange={ev =>
										this.setState({ discountValue: this.sanitizeNumber(ev.target.value) })
									}
									style={{ width: 160 }}
									inputProps={discountType === 'percent' ? { min: 0, max: 100 } : { min: 0 }}
									InputProps={{
										endAdornment: discountType === 'coefficient' ? null : (
											<InputAdornment position="end">
												{discountType === 'percent' ? '%' : '€'}
											</InputAdornment>
										),
									}}
								/>
								<p className="bulk-purchase-price-example">
									{I18n.t('productStock.bulkPurchasePrice.example')}
								</p>
							</div>
						)}
					</RadioGroup>
				</DialogContent>
				<DialogActions>
					<Button onClick={onCancel} color="primary">
						<Translate value={'cancel'} />
					</Button>
					<Button
						onClick={this.handleValid}
						disabled={!canValid}
						color="primary"
						variant={'raised'}>
						<Translate value={'validate'} />
					</Button>
				</DialogActions>
			</Dialog>
		);
	}
}

PurchasePriceBulkEdit.propTypes = {
	open: PropTypes.bool.isRequired,
	selectedProducts: PropTypes.array.isRequired,
	taxes: PropTypes.array,
	onCancel: PropTypes.func.isRequired,
	onValid: PropTypes.func.isRequired,
};

PurchasePriceBulkEdit.defaultProps = {
	taxes: [],
};

export default PurchasePriceBulkEdit;
